import { ILucaUsageSnapshot } from '@query'

export interface LucaUsageDerived {
  planCredits: number
  planUsed: number
  addonCredits: number
  addonUsed: number
  balanceGranted: number
  balanceRemaining: number
  balanceUsed: number
  overageUsed: number
  totalCredits: number
  showOverageBar: boolean
  usedCredits: number
  creditLimit: number | null
  limitReached: boolean
  barCapacity: number
  limitPercent: number | null
  headroomCredits: number
}

export type ConsumptionKind = 'plan' | 'addon' | 'balance' | 'overage' | 'headroom'

interface ConsumptionSegment {
  kind: ConsumptionKind
  flex: boolean
  widthPercent: number
  fillPercent: number
}

const nonNegative = (value: number): number => Math.max(Number.isFinite(value) ? value : 0, 0)

const ratioPercent = (used: number, cap: number): number => (cap > 0 ? (used / cap) * 100 : 0)

const fillPercentFor = (kind: ConsumptionKind, view: LucaUsageDerived): number => {
  switch (kind) {
    case 'plan':
      return ratioPercent(view.planUsed, view.planCredits)
    case 'addon':
      return ratioPercent(view.addonUsed, view.addonCredits)
    case 'balance':
      return ratioPercent(view.balanceUsed, view.balanceGranted)
    case 'overage':
      return 100
    case 'headroom':
      return 0
    default: {
      const _exhaustive: never = kind
      return _exhaustive
    }
  }
}

export const deriveLucaUsageView = (
  snapshot: ILucaUsageSnapshot,
  creditCap: number | null = null
): LucaUsageDerived => {
  const planCredits = nonNegative(snapshot.allowance.plan_credits)
  const addonCredits = nonNegative(snapshot.allowance.addon_credits)
  const includedCap = planCredits + addonCredits
  const usedCredits = nonNegative(snapshot.used_credits)
  const includedUsed = Math.min(usedCredits, includedCap)
  const planUsed = Math.min(includedUsed, planCredits)
  const addonUsed = includedUsed - planUsed
  const afterIncluded = usedCredits - includedUsed
  // `manual_credits` is the whole granted or purchased balance, not remaining after usage.
  const balanceGranted = nonNegative(snapshot.allowance.manual_credits)
  const balanceUsed = Math.min(afterIncluded, balanceGranted)
  const balanceRemaining = balanceGranted - balanceUsed
  const overageUsed = afterIncluded - balanceUsed
  const totalCredits = includedCap + balanceGranted
  const creditLimit = creditCap != null ? nonNegative(creditCap) : null
  const usageScale = totalCredits + overageUsed
  const barCapacity = creditLimit == null ? usageScale : Math.max(usageScale, creditLimit)
  const headroomCredits = creditLimit == null ? 0 : Math.max(barCapacity - usageScale, 0)
  const limitPercent =
    creditLimit != null && barCapacity > 0 ? (creditLimit / barCapacity) * 100 : null
  const limitReached = creditLimit != null && usedCredits >= creditLimit

  return {
    planCredits,
    planUsed,
    addonCredits,
    addonUsed,
    balanceGranted,
    balanceRemaining,
    balanceUsed,
    overageUsed,
    totalCredits,
    showOverageBar: overageUsed > 0,
    usedCredits,
    creditLimit,
    limitReached,
    barCapacity,
    limitPercent,
    headroomCredits
  }
}

export const buildConsumptionSegments = (view: LucaUsageDerived): ConsumptionSegment[] => {
  const buckets: { kind: ConsumptionKind; size: number }[] = []
  if (view.planCredits > 0) {
    buckets.push({ kind: 'plan', size: view.planCredits })
  }
  if (view.addonCredits > 0) {
    buckets.push({ kind: 'addon', size: view.addonCredits })
  }
  if (view.balanceGranted > 0) {
    buckets.push({ kind: 'balance', size: view.balanceGranted })
  }
  if (view.showOverageBar) {
    buckets.push({ kind: 'overage', size: view.overageUsed })
  }
  if (view.headroomCredits > 0) {
    buckets.push({ kind: 'headroom', size: view.headroomCredits })
  }

  const capacity =
    view.barCapacity > 0 ? view.barCapacity : buckets.reduce((sum, bucket) => sum + bucket.size, 0)
  const flexKind: ConsumptionKind = buckets[0]?.kind ?? 'plan'

  return buckets.map(({ kind, size }) => ({
    kind,
    flex: kind === flexKind,
    widthPercent: ratioPercent(size, capacity),
    fillPercent: fillPercentFor(kind, view)
  }))
}
